import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import type { ColumnDef } from "@tanstack/react-table";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Input } from "@common/Form/Input";
import { Table } from "@common/Table";
import { Title, ErrorState } from "@common/index";
import { ComponentSize } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useOrderApi } from "@/hooks/apiHooks/useOrderApi";
import { useMountRead } from "@/hooks/useMountRead";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { saveBlob } from "@utils/fileDownload";
import {
    ordersListAtom,
    isFetchingOrdersListAtom,
} from "@/store/orderAtoms";
import {
    DEFAULT_ORDERS_QUERY,
    OrderStatusType,
    type AdminOrderListItem,
    type GetOrdersQuery,
} from "@models/order";
import { currencyFormatter, formatDate, formatOrderNumber, sanitizeOrderSearch } from "@utils/formatter";
import { StatusBadge } from "./_components/StatusBadge";
import { StatusFilterChips } from "./_components/StatusFilterChips";
import {
    ENUM_TO_STATUS_PARAM,
    STATUS_PARAM_TO_ENUM,
} from "./_components/status";
import { parseOrderSort, toOrdersQuerySort } from "./_helpers/orderSort";
import { OrdersBulkBar } from "./_components/OrdersBulkBar";
import { MarkProcessedDialog } from "./_components/MarkProcessedDialog";
import { MarkProcessedResults } from "./_components/MarkProcessedResults";
import {
    SelectAllHeader,
    SelectRowCell,
} from "./_components/OrderSelectCells";
import {
    OrderSelectionContext,
    type OrderSelectionValue,
} from "./_components/orderSelectionContext";
import {
    planMarkProcessed,
    runMarkProcessed,
    type MarkProcessedResult,
    type PlannedRow,
} from "./_helpers/markProcessedRun";
import { buildOrdersCsv, ordersCsvFileName } from "./_helpers/ordersCsv";

// The ticked rows (#473), and the list query they were ticked on.
interface Selection {
    queryKey: string;
    ids: ReadonlySet<string>;
}

const NO_SELECTION: Selection = { queryKey: "", ids: new Set() };

// A constant, with module-level header and cell components, so every render
// hands TanStack the same components and the checkboxes stay mounted.
const SELECT_COLUMN: ColumnDef<AdminOrderListItem> = {
    id: "select",
    size: 64,
    enableSorting: false,
    header: SelectAllHeader,
    cell: SelectRowCell,
};

const countMarkable = (plan: PlannedRow[]): number =>
    plan.filter((row) => row.skipReason === null).length;

export const Orders = () => {
    const { getOrderList, getOrderStatuses, markOrderProcessed } =
        useOrderApi();
    const { showSuccess } = useNotification();
    const runGuarded = useSubmitGuard();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { items, totalPages, count, allCount, hasPreviousPage, hasNextPage } =
        useAtomValue(ordersListAtom);
    const isFetching = useAtomValue(isFetchingOrdersListAtom);

    const currentPage = Number(searchParams.get("page")) || 1;
    const currentPageSize =
        Number(searchParams.get("pageSize")) ||
        DEFAULT_ORDERS_QUERY.pageSize!;
    const searchTerm = searchParams.get("search") || "";
    const statusParam = searchParams.get("status");
    const activeStatus: OrderStatusType | null = statusParam
        ? (STATUS_PARAM_TO_ENUM[statusParam] ?? null)
        : null;
    const { columnId: sortColumn, desc: sortDesc } = parseOrderSort(
        searchParams.get("sortBy"),
        searchParams.get("sortDirection"),
    );

    // Debounce search input to avoid a request per keystroke.
    const [searchQuery, setSearchQuery] = useState<string>(searchTerm);

    // Keep the input in sync when the URL changes outside of typing
    // (browser back/forward, cleared filters).
    useEffect(() => {
        setSearchQuery(searchTerm);
    }, [searchTerm]);

    // Every column sorts (ruling O5; its id is its ORDER_SORT_BY key), and a
    // first click is always ascending: left to TanStack, the first direction
    // follows the first row's value type (string ascending, anything else,
    // or no rows, descending).
    const columns = useMemo<ColumnDef<AdminOrderListItem>[]>(
        () => [
            {
                accessorKey: "orderNumber",
                header: "Order #",
                sortDescFirst: false,
                cell: ({ row }) => (
                    <button
                        type="button"
                        className="font-bold text-nowrap text-primary text-left hover:underline"
                        onClick={() =>
                            navigate(
                                ROUTES.ADMIN_ORDERS_DETAIL(row.original.id),
                            )
                        }
                    >
                        {row.original.orderNumberFormatted ??
                            formatOrderNumber(row.original.orderNumber)}
                    </button>
                ),
            },
            {
                accessorKey: "organizationName",
                header: "Customer",
                sortDescFirst: false,
            },
            {
                accessorKey: "locationName",
                header: "Location",
                sortDescFirst: false,
            },
            {
                accessorKey: "status",
                header: "Status",
                sortDescFirst: false,
                cell: ({ row }) => <StatusBadge status={row.original.status} />,
            },
            {
                accessorKey: "total",
                header: "Total",
                sortDescFirst: false,
                cell: ({ getValue }) =>
                    currencyFormatter.format(getValue() as number),
            },
            {
                accessorKey: "createdAt",
                header: "Created",
                sortDescFirst: false,
                cell: ({ getValue }) => formatDate(getValue() as string),
            },
            {
                accessorKey: "lastUpdatedAt",
                header: "Last updated",
                sortDescFirst: false,
                cell: ({ getValue }) => formatDate(getValue() as string | null),
            },
        ],
        [navigate],
    );
    const tableColumns = useMemo(() => [SELECT_COLUMN, ...columns], [columns]);

    // Write the debounced search term into the URL (resetting pagination).
    useEffect(() => {
        if (searchQuery !== searchTerm) {
            const timer = setTimeout(() => {
                const next = new URLSearchParams(searchParams);
                if (searchQuery) {
                    next.set("search", searchQuery);
                } else {
                    next.delete("search");
                }
                next.delete("page");
                setSearchParams(next);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [searchQuery, searchTerm, searchParams, setSearchParams]);

    const fetchOrders = useCallback(() => {
        const query: GetOrdersQuery = {
            ...DEFAULT_ORDERS_QUERY,
            pageNumber: currentPage,
            pageSize: currentPageSize,
            search: sanitizeOrderSearch(searchTerm) || undefined,
            ...toOrdersQuerySort({ columnId: sortColumn, desc: sortDesc }),
        };

        // "All" omits the status filter entirely (None=0 is never sent).
        if (activeStatus) {
            query.status = activeStatus;
        }

        return getOrderList(query);
    }, [
        currentPage,
        currentPageSize,
        searchTerm,
        activeStatus,
        sortColumn,
        sortDesc,
        getOrderList,
    ]);

    // Issued on mount and on every query change; the page shows a failure
    // only for its own read (#407).
    const { readStatus, reload } = useMountRead(fetchOrders);
    const isListLoading = isFetching || readStatus === "loading";

    // Bulk actions (#473). A selection belongs to the list query it was made
    // on, so any page, page-size, filter, search or sort change (browser
    // back/forward included) clears it, and returning to that query does not
    // bring it back. It is cleared during render, with no effect: React
    // re-runs this render with the cleared state before committing, so the
    // old selection is never shown against another query. "All" means the
    // rows on this page only.
    const queryKey = JSON.stringify([
        currentPage,
        currentPageSize,
        searchTerm,
        activeStatus,
        sortColumn,
        sortDesc,
    ]);
    const [selection, setSelection] = useState<Selection>(NO_SELECTION);
    if (selection.queryKey !== queryKey && selection.ids.size > 0) {
        setSelection(NO_SELECTION);
    }
    const selectedIds = selection.ids;
    // In the page's row order.
    const selectedRows = items.filter((item) => selectedIds.has(item.id));
    const allSelected =
        items.length > 0 && selectedRows.length === items.length;
    const selectedPlan = planMarkProcessed(selectedRows);
    const selectedMarkableCount = countMarkable(selectedPlan);

    // The confirm's plan (null: closed), the run's position (null: not
    // running) and the last run's per-order outcomes (null: none to show).
    const [markPlan, setMarkPlan] = useState<PlannedRow[] | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [markResults, setMarkResults] = useState<
        MarkProcessedResult[] | null
    >(null);
    const isRunning = progress !== null;
    const planMarkableCount = markPlan ? countMarkable(markPlan) : 0;
    // Where the dialog returns focus after a run: the results mount in the
    // same commit that closes it, and are never disabled.
    const resultsRef = useRef<HTMLDivElement | null>(null);

    const toggleRow = (id: string) => {
        const ids = new Set(selectedIds);
        if (ids.has(id)) {
            ids.delete(id);
        } else {
            ids.add(id);
        }
        setSelection({ queryKey, ids });
    };

    const toggleAll = () =>
        setSelection({
            queryKey,
            ids: allSelected ? new Set() : new Set(items.map((item) => item.id)),
        });

    const handleMarkProcessedConfirm = () =>
        runGuarded(async () => {
            if (!markPlan) return;

            const results = await runMarkProcessed(
                markPlan,
                markOrderProcessed,
                setProgress,
            );

            setMarkResults(results);
            setMarkPlan(null);
            setProgress(null);
            setSelection(NO_SELECTION);
            // The list and the header's Received count both changed.
            void reload();
            getOrderStatuses().catch(() => {
                /* the header count is non-critical chrome */
            });
        });

    const handleExport = () => {
        const count = selectedRows.length;
        saveBlob(
            new Blob([buildOrdersCsv(selectedRows)], {
                type: "text/csv;charset=utf-8",
            }),
            ordersCsvFileName(new Date()),
        );
        showSuccess({
            message: `Exported ${count} ${count === 1 ? "order" : "orders"}.`,
        });
    };

    const selectionValue: OrderSelectionValue = {
        selectedIds,
        allSelected,
        partlySelected: selectedRows.length > 0 && !allSelected,
        isEmpty: items.length === 0,
        isRunning,
        isListLoading,
        toggleRow,
        toggleAll,
    };

    const handleStatusSelect = (status: OrderStatusType | null) => {
        const next = new URLSearchParams(searchParams);
        const key = status ? ENUM_TO_STATUS_PARAM[status] : undefined;
        if (key) {
            next.set("status", key);
        } else {
            next.delete("status");
        }
        // Any filter change resets pagination.
        next.delete("page");
        setSearchParams(next);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleSortingChange = (
        columnId: string | null,
        direction: "asc" | "desc" | null,
    ) => {
        // With removal off, the Table always reports a column and a direction.
        if (!columnId || !direction) return;

        const next = new URLSearchParams(searchParams);
        next.set("sortBy", columnId);
        next.set("sortDirection", direction);
        // A new order makes the current page a different slice: start over.
        next.delete("page");
        setSearchParams(next);
    };

    const handlePageChange = (page: number) => {
        const next = new URLSearchParams(searchParams);
        next.set("page", page.toString());
        setSearchParams(next);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        const next = new URLSearchParams(searchParams);
        next.set("pageSize", newPageSize.toString());
        next.delete("page");
        setSearchParams(next);
    };

    return (
        <div className="px-10">
            <div className="grid grid-cols-12 items-center gap-4">
                <Title>Orders</Title>
                <div className="col-span-8 col-start-5 flex justify-end">
                    <Input
                        value={searchQuery}
                        name="orderSearch"
                        placeholder="Search by order #, customer, or tracking..."
                        icon={faMagnifyingGlass}
                        type="search"
                        onChange={handleSearch}
                    />
                </div>
            </div>

            <StatusFilterChips
                active={activeStatus}
                onSelect={handleStatusSelect}
            />

            {/* Outside the read's branches: a failed reload after a run
                must not hide what the run did. */}
            {markResults && (
                <MarkProcessedResults
                    ref={resultsRef}
                    results={markResults}
                    onDismiss={() => setMarkResults(null)}
                />
            )}

            {readStatus === "failed" ? (
                <ErrorState onRetry={reload} />
            ) : (
                <>
                    {selectedRows.length > 0 && (
                        <OrdersBulkBar
                            selectedCount={selectedRows.length}
                            markableCount={selectedMarkableCount}
                            onMarkProcessed={() => setMarkPlan(selectedPlan)}
                            onExport={handleExport}
                            onClear={() => setSelection(NO_SELECTION)}
                        />
                    )}
                    <OrderSelectionContext value={selectionValue}>
                        <Table
                            data={items}
                            columns={tableColumns}
                            size={ComponentSize.XS}
                            pagination={true}
                            isLoading={isListLoading}
                            sortingProps={{
                                onSortingChange: handleSortingChange,
                                sorting: [{ id: sortColumn, desc: sortDesc }],
                                enableSortingRemoval: false,
                            }}
                            paginationProps={{
                                currentPage: currentPage,
                                totalPages: totalPages,
                                pageSize: currentPageSize,
                                pageSizeOptions: [10, 25, 50, 100],
                                count: count,
                                allCount: allCount,
                                onPageChange: handlePageChange,
                                onPageSizeChange: handlePageSizeChange,
                                hasPreviousPage: hasPreviousPage,
                                hasNextPage: hasNextPage,
                            }}
                        />
                    </OrderSelectionContext>
                </>
            )}

            {markPlan && (
                <MarkProcessedDialog
                    markableCount={planMarkableCount}
                    skippedCount={markPlan.length - planMarkableCount}
                    progress={progress}
                    returnFocusRef={resultsRef}
                    onConfirm={() => void handleMarkProcessedConfirm()}
                    onClose={() => setMarkPlan(null)}
                />
            )}
        </div>
    );
};
